
import { useState } from "react";
import { DevotionalHeader } from "./DevotionalHeader";
import { useSession } from "@supabase/auth-helpers-react";
import Confetti from 'react-confetti';
import { DevotionalRecord, DevotionalProgressRecord, DevotionalFormValues } from "./types";
import { DevotionalForm } from "./DevotionalForm";
import { DevotionalCompletion } from "./DevotionalCompletion";
import { useDevotionalCompletion } from "./completion/useDevotionalCompletion";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@/components/ui/theme-provider";

interface DailyDevotionalProps {
  devotional: DevotionalRecord;
  progress?: DevotionalProgressRecord;
  onProgressUpdate?: () => void;
}

export function DailyDevotional({ 
  devotional, 
  progress,
  onProgressUpdate 
}: DailyDevotionalProps) {
  const [isCompleted, setIsCompleted] = useState(Boolean(progress?.completed_at));
  const session = useSession();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { theme } = useTheme();

  const { 
    isSubmitting, 
    showConfetti, 
    handleCompletion 
  } = useDevotionalCompletion({
    userId: session?.user?.id || '',
    devotionalId: devotional.id,
    points: devotional.points,
    onComplete: () => {
      setIsCompleted(true);
      onProgressUpdate?.();
    }
  });

  const onSubmit = async (values: DevotionalFormValues) => {
    if (!session?.user?.id) {
      toast({
        title: "Sign in required",
        description: "Please sign in to save your progress and track your devotional journey.",
        action: (
          <ToastAction 
            altText="Sign In"
            onClick={() => navigate("/premium")}
          >
            Sign In
          </ToastAction>
        ),
      });
      return;
    }

    if (isCompleted) {
      toast({
        title: "Already Completed",
        description: "You've already completed this devotional. Come back tomorrow!",
      });
      return;
    }

    await handleCompletion(values);
  };

  const getTextColor = () => {
    return theme === "dark" ? "text-white" : "text-app-text-primary";
  };

  const getCardClasses = () => {
    if (theme === "dark") {
      return "card-glass p-6 rounded-xl bg-white/10";
    }
    return "card-base p-6 rounded-xl bg-app-surface border-app-border";
  };

  return (
    <div className="space-y-10">
      {showConfetti && <Confetti recycle={false} numberOfPieces={200} />}
      
      <div className="animate-fade-in">
        <DevotionalHeader 
          title={devotional.title}
          scriptureText={devotional.scripture_text}
          scriptureReference={devotional.scripture_reference}
        />
      </div>

      <div className="space-y-6 animate-slide-up" style={{ animationDelay: "0.2s" }}>
        <h3 className={`text-headline mb-4 flex items-center gap-2 ${getTextColor()}`}>
          <span className="text-2xl">🙏</span>
          Today's Prayer
        </h3>
        <div className={getCardClasses()}>
          <p className={`text-body font-medium whitespace-pre-line ${theme === "dark" ? "text-white/90" : "text-app-text-primary"}`}>
            {devotional.prayer_text}
          </p>
        </div>
      </div>

      <div className="space-y-6 animate-slide-up" style={{ animationDelay: "0.4s" }}>
        <h3 className={`text-headline mb-4 flex items-center gap-2 ${getTextColor()}`}>
          <span className="text-2xl">💭</span>
          Reflection
        </h3>
        <div className={getCardClasses()}>
          <p className={`text-body font-medium whitespace-pre-line ${theme === "dark" ? "text-white/90" : "text-app-text-primary"}`}>
            {devotional.reflection_text}
          </p>
        </div>
      </div>

      <div className="animate-slide-up" style={{ animationDelay: "0.6s" }}>
        {session?.user?.id ? (
          // Show interactive form for authenticated users
          !isCompleted ? (
            <DevotionalForm
              devotional={devotional}
              progress={progress}
              onSubmit={onSubmit}
              isSubmitting={isSubmitting}
            />
          ) : (
            <DevotionalCompletion
              daysCompleted={1}
              points={devotional.points}
              currentDay={devotional.day_number}
            />
          )
        ) : (
          // Show sign-in prompt for non-authenticated users
          <div className={`text-center space-y-4 p-6 rounded-xl ${getCardClasses()}`}>
            <h3 className={`text-xl font-semibold ${getTextColor()}`}>Ready to track your progress?</h3>
            <p className={theme === "dark" ? "text-white/80" : "text-app-text-secondary"}>
              Sign in to save your devotional progress, earn points, and track your spiritual growth journey.
            </p>
            <Button 
              onClick={() => navigate("/premium")}
              className="bg-app-accent hover:opacity-90 text-app-accent-foreground px-8 py-2"
            >
              Sign In to Continue
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
